import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

export const useSubmissions = (initialPageSize = 10) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: initialPageSize
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [candidateId, setCandidateId] = useState('');

  const fetchSubmissions = useCallback(async (page = 1, size = pagination.pageSize, sort = sortBy, order = sortOrder, cid = candidateId) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        page_size: size,
        sort_by: sort,
        sort_order: order,
      };
      if (cid) {
        params.candidate_id = cid;
      }

      const response = await apiService.submissions.list(params);

      setSubmissions(response.data.results || []);
      setPagination({
        currentPage: page,
        totalPages: Math.ceil((response.data.count || 0) / size),
        totalCount: response.data.count || 0,
        pageSize: size
      });
    } catch (err) {
      setError('Failed to fetch submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, sortBy, sortOrder, candidateId]);

  const refresh = useCallback(() => {
    fetchSubmissions(pagination.currentPage, pagination.pageSize, sortBy, sortOrder, candidateId);
  }, [fetchSubmissions, pagination.currentPage, pagination.pageSize, sortBy, sortOrder, candidateId]);

  const changePage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchSubmissions(newPage, pagination.pageSize, sortBy, sortOrder, candidateId);
    }
  }, [fetchSubmissions, pagination.totalPages, pagination.pageSize, sortBy, sortOrder, candidateId]);

  const changePageSize = useCallback((newSize) => {
    fetchSubmissions(1, newSize, sortBy, sortOrder, candidateId);
  }, [fetchSubmissions, sortBy, sortOrder, candidateId]);

  const changeSort = useCallback((newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    fetchSubmissions(1, pagination.pageSize, newSortBy, newSortOrder, candidateId);
  }, [fetchSubmissions, pagination.pageSize, candidateId]);

  const searchByCandidateId = useCallback((cid) => {
    setCandidateId(cid);
    fetchSubmissions(1, pagination.pageSize, sortBy, sortOrder, cid);
  }, [fetchSubmissions, pagination.pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchSubmissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    submissions,
    loading,
    error,
    pagination,
    sortBy,
    sortOrder,
    candidateId,
    setCandidateId,
    refresh,
    changePage,
    changePageSize,
    changeSort,
    searchByCandidateId,
  };
};